import { create } from 'zustand'
import {
  getClockPrepUsage,
  type ClockPrepUsageClock,
  type ClockPrepUsageClockItem,
  type ClockPrepUsageItem,
} from '../api/showClockPrepUsage'

type ClockPrepUsageState = {
  stationId: number | null
  clocksByPrepId: Map<number, ClockPrepUsageClock[]>
  lastNow: string | null
  loading: boolean
  error: string | null
  hydrateUsage: (stationId: number, items: ClockPrepUsageItem[], now?: string | null) => void
  ensureUsage: (stationId: number, prepIds: number[]) => Promise<void>
  optimisticAdd: (stationId: number, prepId: number, clock: ClockPrepUsageClock) => void
  reset: () => void
}

const MAX_PREP_IDS = 200

const createInitialState = (): Omit<ClockPrepUsageState, 'ensureUsage' | 'hydrateUsage' | 'optimisticAdd' | 'reset'> => ({
  stationId: null,
  clocksByPrepId: new Map<number, ClockPrepUsageClock[]>(),
  lastNow: null,
  loading: false,
  error: null,
})

const chunk = <T,>(values: T[], size: number): T[][] => {
  const out: T[][] = []
  for (let i = 0; i < values.length; i += size) {
    out.push(values.slice(i, i + size))
  }
  return out
}

const normalizeIds = (ids: number[]): number[] => {
  const unique = new Set<number>()
  ids.forEach((id) => {
    if (Number.isFinite(id) && id > 0) {
      unique.add(Math.floor(id))
    }
  })
  return Array.from(unique)
}

const hasSameClockDay = (a: ClockPrepUsageClock, b: ClockPrepUsageClock): boolean => a.day_id === b.day_id

const sortClockItemList = (values: ClockPrepUsageClockItem[]): ClockPrepUsageClockItem[] =>
  [...values].sort((a, b) => {
    const aStart = typeof a.scheduled_start === 'string' ? a.scheduled_start : ''
    const bStart = typeof b.scheduled_start === 'string' ? b.scheduled_start : ''
    if (aStart && bStart && aStart !== bStart) {
      return aStart < bStart ? -1 : 1
    }
    return a.day_item_id - b.day_item_id
  })

const mergeClockItems = (
  existing: ClockPrepUsageClockItem[] | undefined,
  incoming: ClockPrepUsageClockItem[] | undefined,
): ClockPrepUsageClockItem[] => {
  const safeExisting = Array.isArray(existing) ? existing : []
  const safeIncoming = Array.isArray(incoming) ? incoming : []
  if (safeIncoming.length === 0) {
    return safeExisting
  }
  const map = new Map<number, ClockPrepUsageClockItem>()
  safeExisting.forEach((item) => map.set(item.day_item_id, item))
  safeIncoming.forEach((item) => {
    if (!map.has(item.day_item_id)) {
      map.set(item.day_item_id, item)
    }
  })
  return sortClockItemList(Array.from(map.values()))
}

const sortClockList = (values: ClockPrepUsageClock[]): ClockPrepUsageClock[] =>
  [...values].sort((a, b) => {
    const aStart = typeof a.window_start === 'string' ? a.window_start : ''
    const bStart = typeof b.window_start === 'string' ? b.window_start : ''
    if (aStart && bStart && aStart !== bStart) {
      return aStart < bStart ? -1 : 1
    }
    if (a.day_id !== b.day_id) {
      return a.day_id - b.day_id
    }
    return 0
  })

const mergeClockList = (existing: ClockPrepUsageClock[], clock: ClockPrepUsageClock): ClockPrepUsageClock[] => {
  const index = existing.findIndex((entry) => hasSameClockDay(entry, clock))
  if (index === -1) {
    return sortClockList([...existing, clock])
  }
  const current = existing[index]
  const mergedItems = mergeClockItems(current.items, clock.items)
  if (mergedItems.length === (current.items ?? []).length) {
    return existing
  }
  const next = [...existing]
  next[index] = { ...current, items: mergedItems }
  return next
}

const useShowClockPrepUsageStore = create<ClockPrepUsageState>((set, get) => ({
  ...createInitialState(),
  hydrateUsage: (stationId, items, now) => {
    if (!stationId || stationId <= 0) {
      return
    }
    set((prev) => {
      const next = prev.stationId === null || prev.stationId === stationId
        ? new Map(prev.clocksByPrepId)
        : new Map<number, ClockPrepUsageClock[]>()
      items.forEach((item) => {
        if (item && Number.isFinite(item.prep_item_id) && item.prep_item_id > 0) {
          next.set(item.prep_item_id, Array.isArray(item.clocks) ? item.clocks : [])
        }
      })
      return {
        ...prev,
        stationId,
        clocksByPrepId: next,
        lastNow: typeof now === 'string' && now ? now : prev.lastNow,
        loading: false,
        error: null,
      }
    })
  },
  ensureUsage: async (stationId, prepIds) => {
    if (!stationId || stationId <= 0) {
      return
    }
    const normalized = normalizeIds(prepIds)
    if (normalized.length === 0) {
      return
    }

    const state = get()
    if (state.stationId !== null && state.stationId !== stationId) {
      set(() => ({
        ...createInitialState(),
        stationId,
      }))
    } else if (state.stationId === null) {
      set((prev) => ({ ...prev, stationId }))
    }

    const toFetch = normalized.filter((id) => !get().clocksByPrepId.has(id))
    if (toFetch.length === 0) {
      return
    }

    set((prev) => ({ ...prev, loading: true, error: null }))

    try {
      const groups = chunk(toFetch, MAX_PREP_IDS)
      for (const group of groups) {
        const response = await getClockPrepUsage(stationId, group)
        set((prev) => {
          if (prev.stationId !== null && prev.stationId !== stationId) {
            return prev
          }

          const next = new Map(prev.clocksByPrepId)
          group.forEach((id) => next.set(id, []))
          response.items.forEach((item) => {
            next.set(item.prep_item_id, Array.isArray(item.clocks) ? item.clocks : [])
          })

          return {
            ...prev,
            stationId,
            clocksByPrepId: next,
            lastNow: typeof response.now === 'string' ? response.now : prev.lastNow,
          }
        })
      }

      set((prev) => ({ ...prev, loading: false }))
    } catch (error) {
      set((prev) => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load show clock usage',
      }))
      throw error
    }
  },
  optimisticAdd: (stationId, prepId, clock) => {
    if (!stationId || stationId <= 0 || !prepId || prepId <= 0) {
      return
    }

    set((prev) => {
      if (prev.stationId !== null && prev.stationId !== stationId) {
        return prev
      }
      const next = new Map(prev.clocksByPrepId)
      const existing = next.get(prepId) ?? []
      next.set(prepId, mergeClockList(existing, clock))
      return { ...prev, stationId, clocksByPrepId: next }
    })
  },
  reset: () => set({ ...createInitialState() }),
}))

export default useShowClockPrepUsageStore
